<template>
    <Toaster />
    <div v-html="model.thankYouMessage" v-if="isSubmitted" class="thank-you-message"></div>
    <Button v-if="isSubmitted" as-child><a href="/">Back to home</a></Button>
    <div class="form-wrapper" v-else>
        <legend class="form-legend">
            {{ model.translations["contact.MandatoryFieldsExplanation"] }}
        </legend>
        <form @submit.prevent="onSubmit" class="contact-form">
            <div class="field-group">
                <FormField v-slot="{ componentField }" name="name">
                    <FormItem>
                        <FormControl>
                            <Input type="text" :placeholder="model.translations['contact.NamePlaceholder']"
                                v-bind="componentField" />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                </FormField>
                <FormField v-slot="{ componentField }" name="phone">
                    <FormItem>
                        <FormControl>
                            <Input type="text" :placeholder="model.translations['contact.PhonePlaceholder']"
                                v-bind="componentField" />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                </FormField>
            </div>
            <div class="field-group">
                <FormField v-slot="{ componentField }" name="email">
                    <FormItem>
                        <FormControl>
                            <Input type="text" :placeholder="model.translations['contact.EmailPlaceholder']"
                                v-bind="componentField" />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                </FormField>
                <FormField v-slot="{ componentField }" name="emailConfirmation">
                    <FormItem>
                        <FormControl>
                            <Input type="text" :placeholder="model.translations['contact.NameRepeatPlaceholder']"
                                v-bind="componentField" />
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                </FormField>
            </div>
            <FormField v-slot="{ componentField }" name="postCode">
                <FormItem>
                    <FormControl>
                        <Input type="text" :placeholder="model.translations['contact.PostcodePlaceholder']"
                            v-bind="componentField" />
                    </FormControl>
                    <FormMessage />
                </FormItem>
            </FormField>
            <h3>Your message:</h3>
            <FormField v-slot="{ componentField }" name="message" :validate-on-model-update="false">
                <FormItem>
                    <FormControl>
                        <Textarea type="text" :placeholder="model.translations['contact.Message.Placeholder']"
                            v-bind="componentField" />
                    </FormControl>
                    <FormMessage />
                </FormItem>
            </FormField>
            <div class="buttons">
                <LoadingButton type="submit" :isLoading="isPending">
                    {{ model.translations["contact.SubmitButton"] }}
                </LoadingButton>
            </div>
        </form>
    </div>
</template>
<script setup lang="ts">
import { useForm } from "vee-validate"
import { FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form"
import { toTypedSchema } from "@vee-validate/zod"
import * as z from "zod"
import { Input } from "@/components/ui/input"
import { useMutation } from "@tanstack/vue-query"
import { Toaster } from "@/components/ui/sonner"
import { Textarea } from "@/components/ui/textarea"
import type { IContactFormProps } from "./ContactForm.model"
import { NAME_FIELD_MAX_LENGTH } from "@/contants/constant"
import { onMounted } from "vue"
import { load, ReCaptchaInstance } from "recaptcha-v3"
import { contactForm, type IContactRequest } from "@/api"
import { POST_CODE_REGEX } from "@/contants/formFieldsRegex.constant"
import LoadingButton from "@/components/LoadingButton/LoadingButton.vue"
import { Button } from "@/components/ui/button"

let recaptcha: ReCaptchaInstance

onMounted(() => {
    load(model.reCaptcha?.siteKey ?? "").then(async (recaptchaInstance) => {
        recaptcha = recaptchaInstance
    })
})

const { model, isSubmitted, onIsSubmittedChange } = defineProps<IContactFormProps>()

const formSchema = toTypedSchema(
    z
        .object({
            name: z
                .string(model.translations["generic.FieldRequired"])
                .min(2)
                .max(NAME_FIELD_MAX_LENGTH, { error: model.translations["generic.ValueTooLong"] }),
            phone: z.string(model.translations["generic.FieldRequired"]).min(10),
            email: z.email({
                error: (iss) =>
                    iss.input
                        ? model.translations["generic.FieldEmailInvalid"]
                        : model.translations["generic.FieldRequired"],
            }),
            emailConfirmation: z.email(model.translations["generic.FieldRequired"]),
            postCode: z
                .string(model.translations["generic.FieldRequired"])
                .regex(POST_CODE_REGEX, model.translations["contact.Validation.PostCodeInvalid"]),
            message: z.preprocess(
                (val) => (val === "" ? undefined : val),
                z
                    .string()
                    .min(10, "Message need to be ad least 10 characters long")
                    .max(500, "Message cannot exceed 500 characters")
                    .optional(),
            ),
        })
        .refine((data) => data.email === data.emailConfirmation, {
            message: model.translations["generic.PersonalDetails.EmailsNotMatch"],
            path: ["emailConfirmation"],
        }),
)

const { handleSubmit } = useForm({
    validationSchema: formSchema,
})

const { mutate: contactFormSubmit, isPending } = useMutation({
    mutationFn: (data: IContactRequest) => contactForm(model.submitUrl ?? "", data),
    onSuccess: () => {
        onIsSubmittedChange(true)
    },
    onError: (error) => {
        console.log(model.translations["generic.Error"])
    },
})

const onSubmit = handleSubmit(async (values) => {
    recaptcha.execute("register").then((token) => {
        contactFormSubmit({
            ...values,
            captchaToken: token,
            currentUmbracoPageId: currentPageId,
        })
    })
})

const currentPageId = window.__STATE__?.currentPageId ?? 0
</script>
<style lang="scss" scoped>
.form-wrapper {
    display: flex;
    flex-direction: column;
}

.form-legend {
    margin-bottom: 5px;
    font-size: 12px;
    color: var(--muted-foreground);
    right: 0;
    align-self: flex-end;
}

.field-group {
    >div {
        flex: 1;
    }

    display: flex;
    gap: 1rem;
    flex-direction: column;
}

@media (min-width: 768px) {
    .field-group {
        flex-direction: row;
    }
}

.contact-form {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.buttons {
    display: flex;
    justify-content: flex-end;
}
</style>
